/**
 * The one place the SPA talks to the backend. Every call goes to `/api/*`: in
 * dev Vite proxies that to the API on :8080; in production CloudFront routes it
 * to the ALB, so the app is same-origin everywhere and no CORS config exists.
 *
 * Cookies carry the session, so requests send credentials but never read or
 * store a token in JS — the session cookie is httpOnly and invisible here.
 */
const BASE = '/api';

/** A non-2xx response, carrying the backend's machine-readable `error` code. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    /** The full parsed error body, for routes that return extra detail. */
    readonly details?: unknown,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;
  const response = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: body === undefined ? undefined : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  const payload: unknown = response.status === 204 ? null : await response.json().catch(() => null);

  if (!response.ok) {
    const errorField =
      typeof payload === 'object' && payload !== null
        ? (payload as { error?: unknown }).error
        : undefined;
    const code = typeof errorField === 'string' ? errorField : `http_${response.status}`;
    throw new ApiError(response.status, code, payload);
  }
  return payload as T;
}
